import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Stack, TextField, PrimaryButton, Spinner, SpinnerSize, Dropdown, IDropdownOption } from "@fluentui/react";
import { getAddUserData, addUser } from "../../../api/admin/api_admin";
import { AddUserResponse, Group } from "../../../api/admin/models_admin";
import { AdminLabel, ErrorDialog, LinkButton } from "../components";
import { validateAuthorityLevel, validatePassword, validateUserId, validateUsernameAlphabet } from "../utils";

export const AddUser = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<AddUserResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState<boolean>(false);

    // Form states
    const [userid, setUserid] = useState<string>("");
    const [username, setUsername] = useState<string>("");
    const [usernameAlphabet, setUsernameAlphabet] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [authorityLevel, setAuthorityLevel] = useState<string>("");
    const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getAddUserData();
            if ("error" in result) {
                setError(result.error);
            } else {
                setData(result);
            }
        } catch (err) {
            setError("データの取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    const handleGroupChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption): void => {
        if (option) {
            setSelectedGroups(
                option.selected ? [...selectedGroups, option.key as string] : selectedGroups.filter(key => key !== option.key)
            );
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        setError(null);

        // バリデーション
        if (!userid) {
            setError("ユーザーIDは必須です");
            setSubmitting(false);
            return;
        }
        if (!validateUserId(userid)) {
            setError("ユーザーIDは半角英数字（大文字・小文字）と @ - _ . のみ使用できます");
            setSubmitting(false);
            return;
        }
        if (!username) {
            setError("ユーザー名は必須です");
            setSubmitting(false);
            return;
        }
        if (data?.is_strict_pswd_policy && !usernameAlphabet) {
            setError("姓名(ローマ字)は必須です");
            setSubmitting(false);
            return;
        }
        if (usernameAlphabet && !validateUsernameAlphabet(usernameAlphabet)) {
            setError("姓名(ローマ字)は半角英数字（大文字・小文字）と半角スペースのみ使用できます");
            setSubmitting(false);
            return;
        }
        if (!password) {
            setError("パスワードは必須です");
            setSubmitting(false);
            return;
        }
        if (!validatePassword(password)) {
            setError("パスワードは半角英数字記号（大文字・小文字）のみ使用できます");
            setSubmitting(false);
            return;
        }
        if (!authorityLevel) {
            setError("権限レベルは必須です");
            setSubmitting(false);
            return;
        }
        if (!validateAuthorityLevel(authorityLevel)) {
            setError("権限レベルは一桁の半角数字(1-9)のみ使用できます");
            setSubmitting(false);
            return;
        }

        try {
            const result = await addUser({
                userid,
                username,
                username_alphabet: usernameAlphabet,
                password,
                authority_level: authorityLevel,
                groups: selectedGroups
            });

            if (result.success) {
                navigate("/menu/users");
            } else {
                setError(result.message || "登録に失敗しました");
            }
        } catch (err) {
            setError("登録中にエラーが発生しました");
        } finally {
            setSubmitting(false);
        }
    };

    const getGroupOptions = (): IDropdownOption[] => {
        if (!data) return [];
        return data.group_list.map((group: Group) => ({
            key: group.group_cd,
            text: `${group.group_cd} - ${group.group_name}`
        }));
    };

    if (loading) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: '400px' }}>
                <Spinner size={SpinnerSize.large} label="読み込み中..." />
            </Stack>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <AdminLabel text="ユーザー登録" />
            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            <Stack tokens={{ childrenGap: 15 }} styles={{ root: { maxWidth: 600 } }}>
                <TextField
                    name="userid"
                    label="ユーザーID"
                    value={userid}
                    onChange={(_, newValue) => setUserid(newValue || "")}
                    required
                    maxLength={100}
                    description="※使用できる文字は、半角英数字（大文字・小文字）と @ - _ . のみです。"
                    autoComplete="off"
                />

                <TextField
                    name="username"
                    label="ユーザー名"
                    value={username}
                    onChange={(_, newValue) => setUsername(newValue || "")}
                    required
                    maxLength={100}
                    description="※100字以下で入力してください。"
                    autoComplete="off"
                />

                <TextField
                    name="username_alphabet"
                    label="姓名(ローマ字)"
                    value={usernameAlphabet}
                    onChange={(_, newValue) => setUsernameAlphabet(newValue || "")}
                    required={data?.is_strict_pswd_policy}
                    maxLength={100}
                    description="※使用できる文字は、半角英数字（大文字・小文字）と半角スペースのみです。"
                    autoComplete="off"
                />

                <TextField
                    name="password"
                    label="パスワード"
                    type="password"
                    value={password}
                    onChange={(_, newValue) => setPassword(newValue || "")}
                    required
                    maxLength={100}
                    description="※使用できる文字は半角英数字記号（大文字・小文字）のみです。"
                    canRevealPassword
                    autoComplete="new-password"
                />

                <TextField
                    name="authority_level"
                    label="権限レベル"
                    value={authorityLevel}
                    onChange={(_, newValue) => setAuthorityLevel(newValue || "")}
                    required
                    maxLength={1}
                    description="※使用できる文字は一桁の半角数字のみです。"
                    autoComplete="off"
                />

                <Dropdown
                    label="グループ"
                    placeholder="グループを選択"
                    multiSelect
                    options={getGroupOptions()}
                    selectedKeys={selectedGroups}
                    onChange={handleGroupChange}
                />

                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <PrimaryButton
                        text="登録"
                        onClick={handleSubmit}
                        disabled={submitting || !userid || !username || !password || !authorityLevel}
                    />
                    <LinkButton link_to="/menu/users" text="戻る" />
                </Stack>
            </Stack>
        </Stack>
    );
}